import type { GardenRow, LocationRow, PlantRow } from "@garden/types/db";
import { type MapPinSymbol, isMapPinSymbol } from "../components/MapPin";
import { polygonBbox, normalisedToViewport, parsePosition } from "./geo";

export interface MappedLocation {
  id: string;
  name: string;
  kind: "bed" | "container";
  polygon: ReadonlyArray<readonly [number, number]> | null;
}

export interface MappedPin {
  id: string;
  x: number;
  y: number;
  symbol: MapPinSymbol;
}

export function parseViewport(
  raw: GardenRow["viewport"],
): { width: number; height: number } | null {
  if (
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    typeof (raw as Record<string, unknown>).width === "number" &&
    typeof (raw as Record<string, unknown>).height === "number"
  ) {
    return raw as { width: number; height: number };
  }
  return null;
}

export function parsePolygon(
  raw: LocationRow["polygon"],
): ReadonlyArray<readonly [number, number]> | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const result: Array<readonly [number, number]> = [];
  for (const pt of raw) {
    if (
      Array.isArray(pt) &&
      pt.length >= 2 &&
      typeof pt[0] === "number" &&
      typeof pt[1] === "number"
    ) {
      result.push([pt[0], pt[1]] as const);
    } else {
      return null;
    }
  }
  return result;
}

export function mapLocations(rows: LocationRow[]): MappedLocation[] {
  return rows.map((loc) => ({
    id: loc.id,
    name: loc.name,
    kind: loc.kind,
    polygon: parsePolygon(loc.polygon),
  }));
}

export function computePins(
  plants: PlantRow[],
  locations: MappedLocation[],
): MappedPin[] {
  const pins: MappedPin[] = [];
  for (const plant of plants) {
    if (plant.position == null || plant.location_id == null || plant.symbol == null) continue;
    if (!isMapPinSymbol(plant.symbol)) continue;
    const pos = parsePosition(plant.position);
    if (!pos) continue;
    const parentLocation = locations.find((loc) => loc.id === plant.location_id);
    if (!parentLocation || !parentLocation.polygon) continue;
    const bbox = polygonBbox(parentLocation.polygon);
    const { x, y } = normalisedToViewport(pos, bbox);
    pins.push({ id: plant.id, x, y, symbol: plant.symbol });
  }
  return pins;
}
